import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NONE, Pager, SelectField } from "@/lib/crm";
import { REP_STATUS } from "@/lib/reports";
import { PageHeader, Panel } from "@/components/common/ui-kit";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ReportRow } from "@/components/reports/ReportRow";
import { MonthEndBanner } from "@/components/reports/MonthEndBanner";

const CHIPS = [["bozze", "Bozze"], ["da_confermare", "Da confermare"], ["da_rettificare", "Da rettificare"], ["fatturabili", "Fatturabili", true], ["fatturati", "Fatturati", true]];

export default function ReportsPage() {
  const { can } = useAuth();
  const econ = can("reports:economics");
  const [chip, setChip] = useState(null);
  const [flt, setFlt] = useState({ status: "", q: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [sum, setSum] = useState(null);
  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries({ ...flt, bucket: chip, page }).filter(([, v]) => v));
    api.get("/service-reports", { params: p }).then((r) => setData(r.data)).catch((e) => setData({ items: [], total: 0, pages: 1, error: errorMessage(e) }));
    api.get("/service-reports/summary").then((r) => setSum(r.data)).catch(() => {});
  }, [flt, chip, page]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (v) => { setPage(1); setFlt((s) => ({ ...s, [k]: v === NONE ? "" : v })); };
  return (
    <div data-testid="reports-page" className="pb-20 md:pb-0">
      <PageHeader overline="Operations · Tecnico" title="Rapportini" description="Lavoro svolto, tempo effettivo e tempo fatturabile, conferme e consumo dei pacchetti ore." />
      <MonthEndBanner />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="report-chips">
        {CHIPS.filter((c) => !c[2] || econ).map(([k, l]) => (
          <button key={k} onClick={() => { setPage(1); setChip(chip === k ? null : k); }} data-testid={`report-chip-${k}`}
            className={cn("flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors", chip === k ? "border-brand-500 bg-brand-50 text-brand-700" : "border-line bg-white text-slate-600 hover:bg-slate-50")}>
            {l}<span className="font-mono">{sum?.[k] ?? "·"}</span></button>))}
      </div>
      <Panel className="p-4 md:p-5">
        <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-4">
          <div className="relative md:col-span-3"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><Input value={flt.q} onChange={(e) => set("q")(e.target.value)} placeholder="Numero, cliente o lavoro svolto" className="pl-9" data-testid="report-search" /></div>
          <SelectField value={flt.status || NONE} onChange={set("status")} options={Object.entries(REP_STATUS).map(([k, [l]]) => ({ key: k, label: l }))} allowNone placeholder="Tutti gli stati" testId="report-filter-status" />
        </div>
        {data?.error && <p className="text-sm text-red-600" role="alert">{data.error}</p>}
        {data && !data.items.length && <p className="py-8 text-center text-sm text-slate-500" data-testid="report-empty">Nessun rapportino.</p>}
        <ul className="divide-y divide-line" data-testid="report-list">{data?.items.map((r) => <ReportRow key={r.id} rep={r} />)}</ul>
        {data && <Pager page={page} pages={data.pages} total={data.total} onPage={setPage} unit="rapportini" />}
      </Panel>
    </div>
  );
}
